/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {DEFAULT_RENDERING_FUNCTIONS, defaultListQueryFunctionFactory, keyCodes, QueryFunction, TrivialUnitBox} from "trivial-components";

import {createUiCurrencyValueConfig, UiCurrencyValueConfig} from "../../generated/UiCurrencyValueConfig";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {UiCurrencyFieldConfig, UiCurrencyFieldSubCommandHandler, UiCurrencyFieldSubEventSource} from "../../generated/UiCurrencyFieldConfig";
import {UiField} from "./UiField";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {CURRENCIES, Currency} from "../util/currencies";
import {formatDecimalNumber} from "../Common";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {UiTextInputHandlingField_SpecialKeyPressedSubEvent, UiTextInputHandlingField_TextInputSubEvent} from "../../generated/UiTextInputHandlingFieldConfig";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {SubEventFactory} from "../../generated/SubEventFactory";
import {UiSpecialKey} from "../../generated/UiSpecialKey";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiCurrencyField extends UiField<UiCurrencyFieldConfig, UiCurrencyValueConfig> implements UiCurrencyFieldSubEventSource, UiCurrencyFieldSubCommandHandler {

	public readonly onTextInput: TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent>(this, 250);
	public readonly onSpecialKeyPressed: TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent>(this, 250);

	private trivialUnitBox: TrivialUnitBox<Currency>;
	private $originalInput: JQuery;

	private currencyCodeList: string[];
	private showCurrencySymbol: boolean;
	private defaultCurrencyCode: string;
	
	private queryFunction: QueryFunction<Currency>;

	protected initialize(config: UiCurrencyFieldConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.$originalInput = $('<input type="text" autocomplete="off">').appendTo(targetElement);

		this.trivialUnitBox = new TrivialUnitBox<Currency>(this.$originalInput, {
			idFunction: entry => entry.code,
			unitValueProperty: 'code',
			unitIdProperty: 'code',
			decimalPrecision: config.precision,
			decimalSeparator: context.config.decimalSeparator,
			thousandsSeparator: context.config.thousandsSeparator,
			unitDisplayPosition: config.showCurrencyBeforeAmount ? 'left' : 'right', // right or left
			entryRenderingFunction: DEFAULT_RENDERING_FUNCTIONS.currencySingleLineLong,
			selectedEntryRenderingFunction: (entry) => {
				if (entry == null) {
					return `<div class="tr-template-currency-single-line-short">-</div>`
				} else if (this.showCurrencySymbol && entry.symbol) {
					return `<div class="tr-template-currency-single-line-short">${entry.symbol} (${entry.code})</div>`;
				} else {
					return `<div class="tr-template-currency-single-line-short">${entry.code}</div>`;
				}
			},
			selectedEntry: $.extend({}, CURRENCIES[this.defaultCurrencyCode]),
			queryOnNonNumberCharacters: config.alphaKeysQueryForCurrency,
			editingMode: this.convertToTrivialComponentsEditingMode(config.editingMode),
			queryFunction: (queryString, callback) => this.queryFunction(queryString, callback)
		});
		$(this.trivialUnitBox.getMainDomElement()).addClass("UiCurrencyField");
		this.trivialUnitBox.onChange.addListener((value: {
			unit: string,
			unitEntry: any,
			amount: number,
			amountAsFloatingPointNumber: number
		}) => {
			this.commit();
		});
		this.trivialUnitBox.getEditor().addEventListener('keyup', (e: KeyboardEvent) => {
			if (e.keyCode !== keyCodes.enter
				&& e.keyCode !== keyCodes.tab
				&& !keyCodes.isModifierKey(e)) {
				this.onTextInput.fire(SubEventFactory.createUiTextInputHandlingField_TextInputSubEvent($(this.trivialUnitBox.getEditor()).val().toString()));
			} else 	if (e.keyCode === keyCodes.escape) {
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ESCAPE));
			} else if (e.keyCode === keyCodes.enter) {
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ENTER));
			}
		});

		this.setDefaultCurrencyCode(config.defaultCurrencyCode);
		this.setCurrencyCodeList(config.currencyCodeList || Object.keys(CURRENCIES));
		this.setShowCurrencySymbol(config.showCurrencySymbol);
	}

	isValidData(v: UiCurrencyValueConfig): boolean {
		return v == null || typeof v === "object";
	}

	private convertToTrivialComponentsEditingMode(editingMode: UiFieldEditingMode) {
		return editingMode === UiFieldEditingMode.READONLY ? 'readonly' : editingMode === UiFieldEditingMode.DISABLED ? 'disabled' : 'editable';
	}

	public getMainDomElement(): JQuery {
		return $(this.trivialUnitBox.getMainDomElement());
	}

	public getFocusableElement(): JQuery {
		return $(this.trivialUnitBox.getMainDomElement()).find(".tr-editor");
	}

	public hasFocus(): boolean {
		return this.getMainDomElement().is('.focus');
	}

	protected displayCommittedValue(): void {
		let uiValue = this.getCommittedValue();
		if (uiValue) {
			this.trivialUnitBox.setSelectedEntry(CURRENCIES[uiValue.currencyCode]);
			this.trivialUnitBox.setAmount(uiValue.value);
		} else {
			this.trivialUnitBox.setAmount(null);
			this.trivialUnitBox.setSelectedEntry(CURRENCIES[this.defaultCurrencyCode]);
		}
	}

	focus(): void {
		this.trivialUnitBox.focus();
		this.getFocusableElement().select();
	}

	destroy(): void {
		this.trivialUnitBox.destroy();
		this.$originalInput.remove();
	}

	getTransientValue(): UiCurrencyValueConfig {
		return createUiCurrencyValueConfig(this.trivialUnitBox.getAmount(), this.trivialUnitBox.getSelectedEntry() && this.trivialUnitBox.getSelectedEntry().code);
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		this.getMainDomElement()
			.removeClass(Object.keys(UiField.editingModeCssClasses).map((key: any) => UiField.editingModeCssClasses[key]).join(" "))
			.addClass(UiField.editingModeCssClasses[editingMode]);
		this.trivialUnitBox.setEditingMode(this.convertToTrivialComponentsEditingMode(editingMode));
	}

	public static getReadOnlyHtml(config: UiCurrencyFieldConfig, value: UiCurrencyValueConfig, context: TeamAppsUiContext, drawBorder: boolean): string {
		let content: string;
		if (value != null) {
			const currency = CURRENCIES[value.currencyCode || config.defaultCurrencyCode || ""];
			const displayedCurrency = config.showCurrencySymbol ? currency.symbol : currency.code;
			const amountAsString = formatDecimalNumber(value.value, config.precision, context.config.decimalSeparator, context.config.thousandsSeparator);
			content = (config.showCurrencyBeforeAmount ? displayedCurrency + ' ' : '') + amountAsString + (config.showCurrencyBeforeAmount ? '' : ' ' + displayedCurrency);
		} else {
			content = "";
		}
		return `<div class="static-readonly-UiCurrencyField ${drawBorder ? "bordered" : ""}">${content}</div>`;
	}

	getDefaultValue() {
		return createUiCurrencyValueConfig(0, this.defaultCurrencyCode);
	}

	setCurrencyCodeList(currencyCodeList: string[]): void {
		this.currencyCodeList = currencyCodeList;
		this.queryFunction = defaultListQueryFunctionFactory(this.currencyCodeList.map((code) => CURRENCIES[code]), ["code", "name", "symbol"], {matchingMode: "contains", ignoreCase: true});
		this.trivialUnitBox.updateEntries(this.currencyCodeList.map((code) => CURRENCIES[code]));
	}

	setShowCurrencyBeforeAmount(showCurrencyBeforeAmount: boolean): void {
		this.trivialUnitBox.setUnitDisplayPosition(showCurrencyBeforeAmount ? 'left' : "right")
	}

	setShowCurrencySymbol(showCurrencySymbol: boolean): void {
		this.showCurrencySymbol = showCurrencySymbol;
		this.trivialUnitBox.setSelectedEntry(this.trivialUnitBox.getSelectedEntry());
	}

	setDefaultCurrencyCode(defaultCurrencyCode: string): void {
		this.defaultCurrencyCode = defaultCurrencyCode;
	}

	public valuesChanged(v1: UiCurrencyValueConfig, v2: UiCurrencyValueConfig): boolean {
		let nullAndNonNull = ((v1 == null) !== (v2 == null));
		let nonNullAndValuesDifferent = (v1 != null && v2 != null && (v1.value !== v2.value || v1.currencyCode !== v2.currencyCode));
		return nullAndNonNull || nonNullAndValuesDifferent;
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiCurrencyField", UiCurrencyField);